/**
 * GIESTO — Galata store stock in product information (YUN-92).
 *
 * `snippets/giesto-galata-stok.liquid` renders, per variant, how many pieces of its barcode the Galata
 * store holds (`adet`, a whole number ≥ 0), when the backend read the store stock (`guncellendi`) and
 * the line texts for the shop locale. This file shows one line, Zara-style, for the shopper's selection
 * and keeps it in step with the product section's own `giesto:variant-change` event
 * (`sections/giesto-store-product.liquid`, detail.selectedOptions):
 *   - a size is selected        → "Galata Mağaza'da bu bedenden 2 adet var" / "Galata Mağaza'da bu beden yok"
 *   - no size selected yet      → no count: "Galata Mağaza'da var: S · M · XL" for the colour on screen
 *     (the live templates set `require_size_selection`, so this is the first paint of every sized product)
 *   - product without sizes     → "Galata Mağaza'da 2 adet var" (hidden at 0)
 * The disclosure/tab, line and directions link stay hidden when:
 *   - `guncellendi` is older than 6 hours (stopped job or cached page: never claim stale stock),
 *   - no size of the colour is in the store and no size is selected,
 *   - the data is missing or malformed.
 *
 * `GiestoGalataStok.galataState`, `.galataSizes` and `.galataText` are pure and covered by
 * `scripts/giesto-galata-stok.test.mjs` (theme) and `shopifyGalataStokTheme.test.ts` (backend CI).
 */
(() => {
  if (globalThis.GiestoGalataStok) return;

  /** Same window as the backend's THEME_STALE_AFTER_MS (backend/src/services/online/shopifyGalataStok.ts). */
  const MAX_AGE_MS = 6 * 60 * 60 * 1000;

  const isFresh = (data, nowMs) => {
    const readAt = typeof data.guncellendi === 'string' ? Date.parse(data.guncellendi) : NaN;
    return Number.isFinite(readAt) && nowMs - readAt <= MAX_AGE_MS;
  };

  /**
   * Whole pieces in the store for one variant entry. `adet` is clamped here too (never negative, never a
   * fraction), so a bad value can only under-claim. An entry from the first, presence-only snippet
   * (`galata: true`, no `adet`) is in the store with an unknown count → null.
   *
   * @returns {number | null}
   */
  const unitsOf = (variant) => {
    const units = variant?.adet;
    if (typeof units === 'number' && Number.isFinite(units)) return Math.max(0, Math.floor(units));
    return variant?.galata === true ? null : 0;
  };

  const variantsOf = (data) => data.variants.filter((variant) => variant && Array.isArray(variant.options));

  const hasValue = (value) => typeof value === 'string' && value.trim() !== '';

  /**
   * In-store sizes of the selected colour, in product order — the "no size selected" line.
   *   null → hide (nothing of that colour in store, stale, malformed, no colour selected)
   *   []   → in store, but the product has no size option (colour-only): the label alone
   *
   * @param {{guncellendi?: unknown, variants?: unknown} | null} data
   * @param {unknown[]} selectedOptions one value per option position (the section's selection)
   * @param {number} colourPosition -1 when the product has no colour option
   * @param {number} nowMs
   * @returns {string[] | null}
   */
  const galataSizes = (data, selectedOptions, colourPosition, nowMs) => {
    if (!data || typeof data !== 'object' || !Array.isArray(data.variants)) return null;
    if (!isFresh(data, nowMs)) return null;

    const position = Number.isInteger(colourPosition) ? colourPosition : -1;
    const colour = position >= 0 && Array.isArray(selectedOptions) ? selectedOptions[position] : null;
    if (position >= 0 && typeof colour !== 'string') return null;

    let inStore = false;
    const sizes = [];
    for (const variant of variantsOf(data)) {
      if (unitsOf(variant) === 0) continue;
      if (position >= 0 && variant.options[position] !== colour) continue;
      inStore = true;
      const size = variant.options
        .filter((value, index) => index !== position && hasValue(value))
        .join(' / ');
      // A colour-only variant leaves no size: drop it, never print an empty entry.
      if (size && !sizes.includes(size)) sizes.push(size);
    }
    return inStore ? sizes : null;
  };

  /**
   * What the line says for the current selection.
   *   null                           → hide
   *   {sizes: string[]}              → no full selection yet (or an unknown count): in-store sizes, no count
   *   {units: number, sized: boolean} → the selection is one variant: its store count. `sized` = a size
   *                                     was picked ("bu bedenden … / bu beden yok"); false = no size option
   *
   * @param {{guncellendi?: unknown, variants?: unknown, defaultOnly?: unknown} | null} data
   * @param {unknown[]} selectedOptions
   * @param {number} colourPosition
   * @param {number} nowMs
   */
  const galataState = (data, selectedOptions, colourPosition, nowMs) => {
    if (!data || typeof data !== 'object' || !Array.isArray(data.variants)) return null;
    if (!isFresh(data, nowMs)) return null;
    const variants = variantsOf(data);
    if (variants.length === 0) return null;

    // A product without options has one variant and no picker: its count is the line.
    if (data.defaultOnly === true) {
      const units = unitsOf(variants[0]);
      if (units === null) return { sizes: [] };
      return units > 0 ? { units, sized: false } : null;
    }

    const position = Number.isInteger(colourPosition) ? colourPosition : -1;
    const selection = Array.isArray(selectedOptions) ? selectedOptions : [];
    const width = variants[0].options.length;
    const complete = width > 0 && Array.from({ length: width }, (_, index) => selection[index])
      .every((value) => typeof value === 'string');

    if (complete) {
      const match = variants.find((variant) =>
        variant.options.length === width && variant.options.every((value, index) => selection[index] === value));
      // A combination the product does not sell (size S of a colour that has no S) is not in the store.
      const units = match ? unitsOf(match) : 0;
      if (units !== null) {
        const sized = selection.slice(0, width).some((value, index) => index !== position && hasValue(value));
        if (sized) return { units, sized: true };
        return units > 0 ? { units, sized: false } : null;
      }
    }

    const sizes = galataSizes(data, selection, position, nowMs);
    return sizes ? { sizes } : null;
  };

  const withCount = (template, units) => (typeof template === 'string' ? template.replace('{n}', String(units)) : '');

  /**
   * @param {{var?: string, bedenAdet?: string, bedenYok?: string, adet?: string} | null | undefined} labels
   *   the snippet's locale texts; `{n}` is the count
   * @param {ReturnType<typeof galataState>} state
   * Without sizes (colour-only, unknown count) the presence label stands alone — never a trailing ": ".
   */
  const galataText = (labels, state) => {
    if (!labels || typeof labels !== 'object' || !state) return '';
    // Shopify's t filter HTML-escapes locale strings. JSON inside a script is
    // raw text, so normalize those escapes before assigning a plain text line.
    const entities = {amp:'&',lt:'<',gt:'>',quot:'"',apos:"'",'#39':"'",'#x27':"'"};
    labels = Object.fromEntries(Object.entries(labels).map(([key, value]) => [key,
      typeof value === 'string' ? value.replace(/&(amp|lt|gt|quot|apos|#39|#x27);/g, (_, entity) => entities[entity]) : value]));
    if (Array.isArray(state.sizes)) {
      if (typeof labels.var !== 'string' || labels.var === '') return '';
      return state.sizes.length ? `${labels.var}: ${state.sizes.join(' · ')}` : labels.var;
    }
    if (!Number.isInteger(state.units) || state.units < 0) return '';
    if (state.sized) return state.units > 0 ? withCount(labels.bedenAdet, state.units) : labels.bedenYok || '';
    return state.units > 0 ? withCount(labels.adet, state.units) : '';
  };

  globalThis.GiestoGalataStok = { MAX_AGE_MS, galataState, galataSizes, galataText };

  if (typeof document === 'undefined') return;

  /** @type {WeakMap<Element, unknown>} */
  const parsed = new WeakMap();

  const dataOf = (block) => {
    if (!parsed.has(block)) {
      let data = null;
      try {
        data = JSON.parse(block.querySelector('[data-giesto-galata-data]')?.textContent || 'null');
      } catch {
        // A malformed payload hides the line; it must never break the buy block.
      }
      parsed.set(block, data);
    }
    return parsed.get(block);
  };

  const colourPositionOf = (root, data) => {
    const fromRoot = Number(root?.dataset?.colourPosition);
    if (root?.dataset?.colourPosition !== undefined && Number.isInteger(fromRoot)) return fromRoot;
    return Number.isInteger(data?.colourPosition) ? data.colourPosition : -1;
  };

  /** First paint: the server's selection, then whatever option buttons are already marked. */
  const initialSelection = (root) => {
    let selection = [];
    try {
      const fromServer = JSON.parse(root?.dataset?.selectedOptions || '[]');
      if (Array.isArray(fromServer)) selection = fromServer.slice();
    } catch {
      /* buttons below still give the colour */
    }
    root?.querySelectorAll('[data-gap-option][data-selected="true"]').forEach((button) => {
      const position = Number(button.dataset.optionPosition);
      if (Number.isInteger(position) && position >= 0) selection[position] = button.dataset.optionValue ?? null;
    });
    return selection;
  };

  const render = (block, root, selection) => {
    const body = block.querySelector('[data-giesto-galata-body]');
    const line = block.querySelector('[data-giesto-galata-line]');
    if (!(body instanceof HTMLElement) || !(line instanceof HTMLElement)) return;
    const data = dataOf(block);
    const state = galataState(data, selection, colourPositionOf(root, data), Date.now());
    const text = galataText(data?.labels, state);
    line.textContent = text;
    // Hide the disclosure and mobile tab as well as its content when unavailable.
    body.hidden = text === '';
    block.hidden = text === '';
    block.dispatchEvent(new CustomEvent('giesto:galata-change', { bubbles: true }));
  };

  const start = () => {
    document.querySelectorAll('[data-giesto-galata]').forEach((block) => {
      const root = block.closest('giesto-store-product');
      render(block, root, initialSelection(root));
    });
  };

  // The section dispatches on connect too, possibly before this deferred file runs — hence start().
  document.addEventListener('giesto:variant-change', (event) => {
    const root = event.target;
    if (!(root instanceof HTMLElement) || !root.matches('giesto-store-product')) return;
    const block = root.querySelector('[data-giesto-galata]');
    if (block) render(block, root, event.detail?.selectedOptions);
  });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
